/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 4 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import React, { createRef, forwardRef, useState, useEffect } from "react";
import { Select, MenuItem, FormControl, TextField, Popover, Checkbox } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import ButtonComponent from "../../form/ButtonComponent";
import Autocomplete from "@mui/material/Autocomplete";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import axiosWrapper from "../../../services/axiosWrapper";
import get from "lodash/get";
import Text from "../Text/Text";
// Type-only, so the module cycle with ./index (which imports this file's types) is erased at build.
import type { QueryOperator } from "./index";

type FilterType = "Text" | "Date" | "Number" | "MultiSelect";

/**
 * A grid row as this popover reads it. The row shape belongs to whichever grid mounts the filter, so
 * it is only ever indexed (by `field`, or through the column's `_valueGetter`), never destructured.
 */
type FilterRow = Record<string, unknown>;

/** The SlickGrid column-header descriptor whose first button carries the filter icon's css class. */
type FilterColumnHeader = { buttons?: Array<{ cssClass?: string }> };

export type ColumnDefinition = {
  field: string;
  _valueGetter?: (row: FilterRow) => unknown;
  header?: FilterColumnHeader;
  filterApplied?: boolean;
};

/** A column as it comes back from `slickGrid.getColumns()` — the header is always populated there. */
type FilterGridColumn = ColumnDefinition & { header: Required<FilterColumnHeader> };

/** The slice of the grid instance this popover reaches, to repaint the filter icon on a column. */
type FilterGridInstance = {
  slickGrid: { getColumns: () => FilterGridColumn[]; setColumns: (columns: FilterGridColumn[]) => void };
};

/** One leaf predicate emitted into the grid's `where` clause. */
type FilterPredicate = {
  field: string;
  operator: string;
  value?: unknown;
  isComplex?: boolean;
  ignoreCase?: boolean;
  ignoreAccent?: boolean;
};

/** A composite (`and` / `or`) group. The outer `and` group nests the per-column `or` groups. */
type FilterPredicateGroup = {
  isComplex: true;
  ignoreCase: boolean;
  ignoreAccent: boolean;
  condition: "and" | "or";
  predicates: Array<FilterPredicate | FilterPredicateGroup>;
};

/** The whole `where` clause: the composite group, `""` when nothing is filtered, or `[]` before build. */
type FilterWhereClause = FilterPredicateGroup | "" | never[];

/**
 * What the date inputs hold: `date` for the single-date operators, `startDate`/`endDate` for
 * `between`. One shape rather than two, because nothing ever tells them apart other than the
 * operator in force — so a stored value can be narrowed to it in a single step.
 */
type FilterDateText = { date?: Dayjs; startDate?: Dayjs; endDate?: Dayjs };

/**
 * What the text / date / multi-select inputs hold, keyed off the column's filter type. The
 * multi-select member is `unknown[]` rather than `string[]`: the array is whatever was stored for
 * the column (a saved layout / `preAppliedFilters` can hold numbers), and it is handed to the
 * predicate builder untouched — only the option list this popover builds itself is all strings.
 */
type FilterText = string | number | unknown[] | FilterDateText | undefined;

/**
 * One column's stored filter state. Kept structurally interchangeable with the grid's own
 * `FilterCondition` (SlickGrid/index.tsx) — same four required members — so the `filterConditions`
 * state and its setter pass across the boundary in both directions. The extra member is optional and
 * therefore does not break that: the grid's copy omits `predicate`, which only this component writes.
 */
export type FilterCondition = {
  id: string;
  type: FilterType;
  option: QueryOperator;
  text: unknown;
  predicate?: FilterPredicate[];
};

type AnchorPosition = { top: number; left: number } | null;
type FilterConditionsMap = Record<string, FilterCondition>;
type FilterOptionKey =
  | "startswith"
  | "endswith"
  | "empty"
  | "equal"
  | "contains"
  | "doesnotstartwith"
  | "doesnotendwith"
  | "doesnotcontain"
  | "notequal"
  | "notempty"
  | "greaterthan"
  | "greaterthanorequal"
  | "lessthan"
  | "lessthanorequal"
  | "between"
  | "null"
  | "notnull";
type DefaultFilterText = string | number | FilterDateText | string[];
type DefaultFilterOption = { option: FilterOptionKey; text: DefaultFilterText };
export type Props = {
  currentColumnId: string;
  setCurrentColumnId: (id: string) => void;
  setFilterPredicates: React.Dispatch<React.SetStateAction<string | Record<string, unknown>>>;
  anchorEl: AnchorPosition;
  setAnchorEl: (pos: AnchorPosition) => void;
  filterType?: FilterType;
  filterOpened: boolean;
  setFilterOpened: (open: boolean) => void;
  remoteDataSource?: string;
  /** The grid's own response hook; it is handed the raw axios response and returns the row array. */
  beforeDataBound?: (data: unknown) => unknown;
  columnDefinitions: ColumnDefinition[];
  setAppliedFilters: React.Dispatch<React.SetStateAction<string[]>>;
  datasetForFetchingOptions: FilterRow[];
  query?: { where?: unknown[]; [key: string]: unknown };
  localData?: boolean;
  appliedFilters: string[];
  filterConditions: FilterConditionsMap;
  setFilterConditions: React.Dispatch<React.SetStateAction<FilterConditionsMap>>;
  getUpdatedFilterConditions: (conditions: FilterConditionsMap) => FilterConditionsMap;
  applyFilter: (
    data: FilterRow[],
    field: string,
    filterOption: string,
    text: FilterText,
    filterType: FilterType,
  ) => FilterRow[];
  /**
   * Conditions applied before the user opens anything. Callers pass a `filterType` alongside the
   * declared members, so it is declared here too — as `unknown`, because it is whatever their saved
   * layout stored and nothing has validated it.
   */
  preAppliedFilters?: Record<string, FilterCondition & { filterType?: unknown }>;
  onFilterConditionsChange?: (activeKeys: string[], conditions: FilterConditionsMap) => void;
  /**
   * KNOWN MISMATCH (left as-is — fixing it changes runtime): the grid declares this prop as
   * `(option: unknown) => ReactNode`, but this component invokes it as `(props, option, columnId)`.
   * A consumer that supplies it therefore receives the `<li>` props where it expects the option
   * value. Typed here by what is actually passed.
   */
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: string, columnId: string) => React.ReactNode;
};

/**
 * Label shown in the option `Select`, keyed by the operator that option emits. Keyed rather than
 * derived from the label (it used to be `label.toLowerCase().replaceAll(/\s+/g, "")`), so the
 * operator keeps its type from the menu all the way into the predicate.
 */
const FILTER_OPTION_LABELS: Record<FilterOptionKey, string> = {
  startswith: "Starts With",
  endswith: "Ends With",
  empty: "Empty",
  equal: "Equal",
  contains: "Contains",
  doesnotstartwith: "Does Not Start With",
  doesnotendwith: "Does Not End With",
  doesnotcontain: "Does Not Contain",
  notequal: "Not Equal",
  notempty: "Not Empty",
  greaterthan: "Greater Than",
  greaterthanorequal: "Greater Than Or Equal",
  lessthan: "Less Than",
  lessthanorequal: "Less Than Or Equal",
  between: "Between",
  null: "Null",
  notnull: "Not Null",
};

/** Which operators each filter type offers, in the order they are listed in the menu. */
const FILTER_MAPPER: Record<FilterType, FilterOptionKey[]> = {
  Text: [
    "startswith",
    "endswith",
    "empty",
    "equal",
    "contains",
    "doesnotstartwith",
    "doesnotendwith",
    "doesnotcontain",
    "notequal",
    "notempty",
  ],
  Date: [
    "equal",
    "greaterthan",
    "greaterthanorequal",
    "lessthan",
    "lessthanorequal",
    "between",
    "notequal",
    "null",
    "notnull",
  ],
  Number: ["equal", "greaterthan", "greaterthanorequal", "lessthan", "lessthanorequal", "notequal", "null", "notnull"],
  MultiSelect: ["equal"],
};

/** The operator carried by a `MenuItem` above; anything else did not come from that menu. */
const isFilterOptionKey = (value: unknown): value is FilterOptionKey =>
  typeof value === "string" && Object.hasOwn(FILTER_OPTION_LABELS, value);

const TEXT_FIELD_DISABLED_OPTIONS = new Set<QueryOperator>(["empty", "null", "notnull", "notempty"]);

const DEFAULT_FILTER_OPTIONS: Record<FilterType, DefaultFilterOption> = {
  Text: { option: "startswith", text: "" },
  Date: { option: "equal", text: { date: dayjs() } },
  Number: { option: "equal", text: 0 },
  MultiSelect: { option: "equal", text: [] },
};

/**
 * The filter type carried by a stored condition. It comes from a caller (a saved layout), so it can
 * name a type this popover has no defaults for — `DEFAULT_FILTER_OPTIONS[type]` is `undefined` for
 * anything else, which is what the `?.` on that lookup used to absorb.
 */
const isFilterType = (value: unknown): value is FilterType =>
  typeof value === "string" && Object.hasOwn(DEFAULT_FILTER_OPTIONS, value);

/** A stored date can arrive already serialised (the grid writes `startDate.format()`), so parse. */
const toDate = (raw: unknown): Dayjs | undefined => {
  if (dayjs.isDayjs(raw)) return raw;
  if (typeof raw === "string" || typeof raw === "number" || raw instanceof Date) {
    const parsed = dayjs(raw);
    return parsed.isValid() ? parsed : undefined;
  }
  return undefined;
};

/**
 * Turn a stored date value into the one shape the inputs and the predicate builders read.
 *
 * A bare Dayjs has to be settled FIRST: `date` is a Dayjs prototype METHOD, so a raw Dayjs answers
 * `"date" in value` as well — unwrapping it as if it were the `{ date }` wrapper hands `toDate`
 * that method, and the stored date is lost. Same ordering as `toFilterDate` in
 * SlickGrid1/filterUtils.ts:64-71 and `toFilterDayjs` in SlickGrid/index.tsx:493-497.
 */
const toFilterDateText = (value: object): FilterDateText => {
  if (dayjs.isDayjs(value)) return { date: value };
  return {
    date: "date" in value ? toDate(value.date) : undefined,
    startDate: "startDate" in value ? toDate(value.startDate) : undefined,
    endDate: "endDate" in value ? toDate(value.endDate) : undefined,
  };
};

/**
 * `FilterCondition.text` crosses the grid boundary as `unknown`. This is the one place that turns
 * it back into something the inputs and the predicate builders can use; anything unrecognised
 * becomes `undefined`, which they already treat as "nothing chosen yet".
 */
const toFilterText = (value: unknown): FilterText => {
  if (typeof value === "string" || typeof value === "number") return value;
  // A multi-select array is passed through entry by entry: what is in it is the caller's, and the
  // predicate builder sends each entry on as the value to match.
  if (Array.isArray(value)) return value;
  if (typeof value === "object" && value !== null) return toFilterDateText(value);
  return undefined;
};

/** The date shape of a filter value; `undefined` for the text / number / multi-select ones. */
const asDateText = (text: FilterText): FilterDateText | undefined =>
  typeof text === "object" && !Array.isArray(text) ? text : undefined;

/**
 * A multi-select cell holds either a plain value or an object labelled by `name`. The label is not
 * always a string — a row can carry a numeric one — so any primitive label is used; a falsy or
 * object-valued `name` falls back to the cell itself, which is what `x?.name || x` did.
 */
const readName = (value: unknown): string | undefined => {
  if (typeof value !== "object" || value === null || !("name" in value)) return undefined;
  const name: unknown = value.name;
  if (typeof name === "string") return name || undefined;
  if (typeof name === "number" || typeof name === "bigint" || typeof name === "boolean") {
    return name ? String(name) : undefined;
  }
  return undefined;
};

/**
 * Chips for the multi-select input. Its option list is `string[]`, so a stored entry that is not a
 * string is shown by its text form; a value that is all strings — everything this popover writes
 * itself — is handed over unchanged, so nothing about the stored value moves.
 */
const toMultiSelectChips = (text: FilterText): string[] => {
  if (!Array.isArray(text)) return [];
  if (text.every((item): item is string => typeof item === "string")) return text;
  return text.map((item) => readName(item) ?? String(item));
};

/**
 * `beforeDataBound` is the grid's own hook, declared `(data: unknown) => unknown`. The option list
 * only indexes the rows it hands back, so a non-array result (or a non-object row) is dropped here
 * and the raw response is used instead.
 */
const toFilterRows = (value: unknown): FilterRow[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const items: unknown[] = value;
  return items.filter((item): item is FilterRow => typeof item === "object" && item !== null);
};

/** The forwarded ref carries the grid handle only while SlickGrid is mounted. */
const isFilterGridInstance = (value: unknown): value is FilterGridInstance =>
  typeof value === "object" && value !== null && "slickGrid" in value;

const FilterComponent = forwardRef<unknown, Props>(
  (
    {
      currentColumnId,
      setCurrentColumnId,
      setFilterPredicates,
      anchorEl,
      setAnchorEl,
      filterType,
      filterOpened,
      setFilterOpened,
      remoteDataSource,
      beforeDataBound,
      columnDefinitions,
      setAppliedFilters,
      datasetForFetchingOptions,
      query,
      localData,
      appliedFilters,
      filterConditions,
      setFilterConditions,
      getUpdatedFilterConditions,
      applyFilter,
      preAppliedFilters,
      onFilterConditionsChange,
      renderOption,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    // Callers always pass an object ref (`useRef`), never a callback ref.
    const gridRef = typeof ref === "object" && ref !== null ? ref : createRef<unknown>();
    const [tempFilterOption, setTempFilterOption] = useState<FilterOptionKey | "">("");
    const [tempFilterText, setTempFilterText] = useState<FilterText>("");
    const [options, setOptions] = useState<string[]>([]);
    const [multiSelectFilteredOptions, setMultiSelectFilteredOptions] = useState<string[]>([]);
    const [multiSelectSearchValue, setMultiSelectSearchValue] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [optionsOpen, setOptionsOpen] = useState<boolean>(false);
    const openPopover = Boolean(anchorEl);
    const popoverId = openPopover ? "filter-popover" : undefined;

    useEffect(() => {
      if (filterOpened) {
        if (appliedFilters.includes(currentColumnId)) {
          // `FilterCondition` is shared with the grid and declares `option`/`text` required, but the
          // effect below seeds an entry with neither the moment a popover opens (the one asserted
          // write in this file), so an entry read back out of `filterConditions` can be missing
          // both — widened on assignment here rather than trusting that declaration. The grid names
          // the same gap: `getUpdatedFilterConditions` takes `StoredFilterCondition`, whose `option`
          // is optional (SlickGrid/index.tsx:65).
          const filterCondition: Partial<FilterCondition> | undefined = filterConditions[currentColumnId];
          if (filterCondition) {
            const storedOption = filterCondition.option;
            const fallbackOption = filterType ? DEFAULT_FILTER_OPTIONS[filterType].option : "";
            setTempFilterText(toFilterText(filterCondition.text));
            setTempFilterOption(isFilterOptionKey(storedOption) ? storedOption : fallbackOption);
          }
        } else if (filterType) {
          setTempFilterOption(DEFAULT_FILTER_OPTIONS[filterType].option);
          setTempFilterText(DEFAULT_FILTER_OPTIONS[filterType].text);
        }
      }
      setFilterOpened(false);
    }, [filterOpened]);

    useEffect(() => {
      if (currentColumnId) {
        if (!filterConditions[currentColumnId]) {
          setFilterConditions((prev) => ({
            ...prev,
            // Seeded before the user picks anything: `option`/`text` are absent, and `type` is only
            // known once a column has been opened. `FilterCondition` is shared with
            // SlickGrid/index.tsx, which reads this exact partial back in
            // `getUpdatedFilterConditions` — making the three members optional has to be done in
            // both declarations at once, so the shape is asserted at this single write.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- pre-pick seed: `option`/`text` have no value yet and `type` is `filterType | undefined`, but all three are required in this file's `FilterCondition` AND in SlickGrid/index.tsx:50, which owns the state this writes into
            [currentColumnId]: {
              ...prev[currentColumnId],
              id: currentColumnId,
              type: filterType,
              predicate: [
                {
                  field: currentColumnId,
                  operator: filterType ? DEFAULT_FILTER_OPTIONS[filterType].option : "equal",
                  value: filterType ? DEFAULT_FILTER_OPTIONS[filterType].text : "",
                },
              ],
            } as FilterCondition,
          }));
        }
      }
    }, [currentColumnId]);

    useEffect(() => {
      handlePreAppliedFilter();
    }, [preAppliedFilters]);

    const buildEmptyNotEmptyPredicate = (columnId: string, option: QueryOperator) => {
      let predicateCondition: "isempty" | "isnotempty" | undefined;
      if (option === "null" || option === "empty") {
        predicateCondition = "isempty";
      } else if (option === "notnull" || option === "notempty") {
        predicateCondition = "isnotempty";
      }
      return predicateCondition
        ? [
            {
              field: columnId,
              operator: predicateCondition,
            },
          ]
        : [];
    };

    const buildDateBetweenPredicates = (columnId: string, filterText: FilterDateText | undefined) => {
      if (!filterText || (!filterText.startDate && !filterText.endDate)) {
        return [
          {
            field: columnId,
            operator: "greaterthanorequal",
            value: dayjs().subtract(1, "day").format(),
          },
          {
            field: columnId,
            operator: "lessthanorequal",
            value: dayjs().subtract(1, "day").format(),
          },
        ];
      }
      const predicates: FilterPredicate[] = [];
      if (filterText.startDate) {
        const startDateStr = dayjs(filterText.startDate).startOf("day").format();
        predicates.push({
          field: columnId,
          operator: "greaterthanorequal",
          value: startDateStr,
        });
      }
      if (filterText.endDate) {
        const endDateStr = dayjs(filterText.endDate).endOf("day").format();
        predicates.push({
          field: columnId,
          operator: "lessthanorequal",
          value: endDateStr,
        });
      }
      if (predicates.length) return predicates;
      return [
        {
          field: columnId,
          operator: "equal",
          value: dayjs().format(),
        },
      ];
    };

    const buildDateEqualPredicates = (columnId: string, filterText: FilterDateText | undefined) => {
      if (!filterText?.date) {
        const defaultStartDate = dayjs().startOf("day").format();
        const defaultEndDate = dayjs().endOf("day").format();
        return [
          {
            field: columnId,
            operator: "greaterthanorequal",
            value: defaultStartDate,
          },
          {
            field: columnId,
            operator: "lessthanorequal",
            value: defaultEndDate,
          },
        ];
      }
      const d = dayjs(filterText.date);
      const startDate = d.startOf("day").format();
      const endDate = d.endOf("day").format();
      return [
        {
          field: columnId,
          operator: "greaterthanorequal",
          value: startDate,
        },
        {
          field: columnId,
          operator: "lessthanorequal",
          value: endDate,
        },
      ];
    };

    const buildDateOtherPredicates = (
      columnId: string,
      option: QueryOperator,
      filterText: FilterDateText | undefined,
    ) => {
      if (!filterText?.date) {
        return [
          {
            field: columnId,
            operator: option,
            value: dayjs().format(),
          },
        ];
      }
      // `toFilterText` has already parsed whatever was stored into a `Dayjs`, so the only thing
      // left to check is whether that parse produced a usable date.
      const formatted = filterText.date.isValid() ? filterText.date.format() : dayjs().format();
      return [
        {
          field: columnId,
          operator: option,
          value: formatted,
        },
      ];
    };

    const buildMultiSelectPredicates = (columnId: string, filterText: FilterText) => {
      const selectedValues = Array.isArray(filterText) ? filterText : [];
      if (selectedValues.length === 0) {
        return [];
      }
      return selectedValues.map((value: unknown) => ({
        field: columnId,
        operator: "equal",
        value,
      }));
    };

    const buildSimplePredicate = (
      columnId: string,
      option: QueryOperator,
      type: FilterType | undefined,
      filterText: FilterText,
    ) => {
      const defaultValue = type === "Number" ? 0 : "";
      let filterValue;
      if (filterText === undefined) {
        filterValue = defaultValue;
      } else if (type === "Number") {
        filterValue = Number(filterText);
      } else {
        filterValue = filterText;
      }
      return [
        {
          field: columnId,
          operator: option,
          value: filterValue,
        },
      ];
    };

    const handlePreAppliedFilter = () => {
      if (preAppliedFilters) {
        const predicateUpdatedFilter = Object.keys(preAppliedFilters).reduce<FilterConditionsMap>((acc, filterKey) => {
          const f = preAppliedFilters[filterKey]!;
          acc[filterKey] = {
            ...structuredClone(f),
            predicate: getTempPredicateArray(
              toFilterText(f.text),
              f.option,
              // A `filterType` the defaults do not cover is treated as no type at all, exactly as
              // the `DEFAULT_FILTER_OPTIONS[type]?.option` lookup downstream used to.
              isFilterType(f.filterType) ? f.filterType : undefined,
              filterKey,
            ),
          };
          return acc;
        }, {});
        setFilterConditions(structuredClone(predicateUpdatedFilter));
        getFilterPredicates(predicateUpdatedFilter, true);
        setAppliedFilters(Object.keys(preAppliedFilters));
      }
    };

    useEffect(() => {
      if (!filterType || !filterConditions[currentColumnId]) return;
      if ((tempFilterOption === "equal" || filterType === "MultiSelect") && filterType !== "Number") {
        fetchOptions("", currentColumnId, "startswith");
      }
      if (optionsOpen && (filterType === "Text" || filterType === "Number") && tempFilterOption !== "equal") {
        fetchOptions(tempFilterText, currentColumnId, tempFilterOption);
      }
    }, [optionsOpen, tempFilterText, tempFilterOption]);

    const handleCloseOptions = () => setOptionsOpen(false);
    const handleClosePopover = () => {
      setTempFilterText("");
      setTempFilterOption("");
      setMultiSelectSearchValue("");
      setMultiSelectFilteredOptions([]);
      if (!appliedFilters.includes(currentColumnId)) {
        setFilterConditions((prev) => {
          const newState = { ...prev };
          delete newState[currentColumnId];
          return newState;
        });
      }
      setAnchorEl(null);
      setOptionsOpen(false);
    };

    const getFilterPredicates = (updatedFilterConditions: FilterConditionsMap, callForData: boolean) => {
      const predicates: Array<FilterPredicate | FilterPredicateGroup> = [];
      let whereClause: FilterWhereClause = [];
      if (Object.keys(updatedFilterConditions).length === 0 && callForData) {
        setFilterPredicates("");
      }
      for (const filterkey of Object.keys(updatedFilterConditions)) {
        const filterCondition = updatedFilterConditions[filterkey]!;
        const enhancedPredicates = (filterCondition.predicate || []).map((predicate) => ({
          ...predicate,
          isComplex: false,
          ignoreCase: true,
          ignoreAccent: false,
        }));
        if (filterCondition.type === "MultiSelect") {
          predicates.push({
            isComplex: true,
            ignoreCase: true,
            ignoreAccent: false,
            condition: "or",
            predicates: enhancedPredicates,
          });
        } else {
          predicates.push(...enhancedPredicates);
        }
      }
      if (predicates.length > 0) {
        whereClause = {
          isComplex: true,
          ignoreCase: true,
          ignoreAccent: false,
          condition: "and",
          predicates,
        };
      }
      if (callForData) {
        // `[]` is the pre-build placeholder for "nothing to filter". Every reader of
        // `filterPredicates` tests `typeof x === "object" && Object.keys(x).length > 0`, so it is
        // handed over as the same `""` this function already sets for an empty condition map.
        setFilterPredicates(Array.isArray(whereClause) ? "" : whereClause);
      }
      return whereClause;
    };

    const getTempPredicateArray = (
      filterText: FilterText,
      // Widened to `QueryOperator` for `preAppliedFilters`, whose conditions come from callers and
      // are not restricted to the operators this popover's menu offers.
      filterOption: QueryOperator | "",
      type: FilterType | undefined,
      columnId: string,
    ): FilterPredicate[] => {
      if (!columnId) return [];
      const option: QueryOperator = filterOption || (type ? DEFAULT_FILTER_OPTIONS[type].option : "equal");
      if (filterOption && TEXT_FIELD_DISABLED_OPTIONS.has(option)) {
        return buildEmptyNotEmptyPredicate(columnId, option);
      }
      if (type === "Date") {
        const dateText = asDateText(filterText);
        if (option === "between") {
          return buildDateBetweenPredicates(columnId, dateText);
        }
        if (option === "equal") {
          return buildDateEqualPredicates(columnId, dateText);
        }
        return buildDateOtherPredicates(columnId, option, dateText);
      }
      if (type === "MultiSelect") {
        return buildMultiSelectPredicates(columnId, filterText);
      }
      return buildSimplePredicate(columnId, option, type, filterText);
    };

    const handleSearchInMultiSelectOptions = (searchText: string) => {
      setMultiSelectSearchValue(searchText);
      if (searchText) {
        const filtered = options.filter(
          (option) => option !== "No Records Found" && option.toLowerCase().includes(searchText.toLowerCase()),
        );
        setMultiSelectFilteredOptions(filtered.length > 0 ? filtered : ["No Records Found"]);
        return;
      }
      setMultiSelectFilteredOptions(options);
    };

    /**
     * Repaints the filter icon on the open column. The grid handle is null until SlickGrid mounts
     * (and again once it unmounts), and there is no header to repaint then — the filter state around
     * each call is updated either way.
     */
    const repaintFilterIcon = (cssClass: string, filterApplied: boolean) => {
      const gridInstance = gridRef.current;
      if (!isFilterGridInstance(gridInstance)) return;
      const updatedColumns = gridInstance.slickGrid.getColumns().map((col) => {
        if (col.field === currentColumnId) {
          col.header.buttons[0]!.cssClass = cssClass;
          col.filterApplied = filterApplied;
        }
        return col;
      });
      gridInstance.slickGrid.setColumns(updatedColumns);
    };

    const clearFilterForCurrentColumn = () => {
      const updatedFilterConditions = { ...filterConditions };
      delete updatedFilterConditions[currentColumnId];
      setFilterConditions(updatedFilterConditions);
      if (Object.keys(updatedFilterConditions).length === 0) {
        setFilterPredicates("");
      }
      repaintFilterIcon("mdi mdi-filter-variant text-slate-900", false);
      setAppliedFilters((prev) => prev.filter((data) => data !== currentColumnId));
      if (Object.keys(updatedFilterConditions).length && remoteDataSource) {
        getFilterPredicates(updatedFilterConditions, true);
      }
    };

    const applyFilterForCurrentColumn = () => {
      if (!filterType) return;
      const predicateArray = getTempPredicateArray(tempFilterText, tempFilterOption, filterType, currentColumnId);
      // The seeding effect above has already written `id`/`type` for the open column; they are
      // restated so this is a complete condition whatever is in state. An option the user never
      // picked resolves to the type's default — the same default every reader (this component and
      // the grid) substitutes for a missing one.
      const nextCondition: FilterCondition = {
        ...filterConditions[currentColumnId],
        id: currentColumnId,
        type: filterType,
        option: tempFilterOption || DEFAULT_FILTER_OPTIONS[filterType].option,
        text: tempFilterText,
        predicate: predicateArray,
      };
      if (currentColumnId) {
        const newFilterConditions = { ...filterConditions, [currentColumnId]: nextCondition };
        setFilterConditions((prev) => ({ ...prev, ...newFilterConditions }));
        setAppliedFilters((prev) => (prev.includes(currentColumnId) ? [...prev] : [...prev, currentColumnId]));
        if (onFilterConditionsChange) {
          onFilterConditionsChange(Object.keys(newFilterConditions), newFilterConditions);
        }
      }
      repaintFilterIcon("mdi mdi-filter-variant text-[var(--primary-gradient-color1)]", true);
      if (Object.keys(filterConditions).length && remoteDataSource) {
        getFilterPredicates({ ...filterConditions, [currentColumnId]: nextCondition }, true);
      }
    };

    const handleApplyFilter = (clearFilter = false) => {
      if (!filterType) return;
      if (clearFilter) {
        clearFilterForCurrentColumn();
      } else {
        applyFilterForCurrentColumn();
      }
      setAnchorEl(null);
      setOptions([]);
      setOptionsOpen(false);
      setTempFilterText("");
      setTempFilterOption("");
      setCurrentColumnId("");
    };

    const getDateValue = (dateValue: Dayjs, dateType: string) => {
      let modifiedDate = dateValue;
      if (dateType === "lessthan") {
        modifiedDate = dateValue.startOf("day");
      } else if (dateType === "lessthanorequal") {
        modifiedDate = dateValue.endOf("day");
      } else if (dateType === "greaterthan") {
        modifiedDate = dateValue.endOf("day");
      } else if (dateType === "greaterthanorequal") {
        modifiedDate = dateValue.startOf("day");
      }
      return modifiedDate;
    };

    const handleFilterChange = (event: SelectChangeEvent<string>) => {
      const v = event.target.value;
      // The menu only renders `FilterOptionKey` values; anything else is not one of this column's
      // operators and must not be stored as one.
      if (!isFilterOptionKey(v)) return;
      setTempFilterOption(v);
      const filterValue: Dayjs = asDateText(tempFilterText)?.date ?? dayjs();
      if (filterType === "Date") {
        if (v === "between") {
          setTempFilterText({ startDate: dayjs(), endDate: dayjs() });
        } else if (v === "equal") {
          setTempFilterText({ date: dayjs() });
        } else {
          setTempFilterText({ date: getDateValue(filterValue, v) });
        }
      }
    };

    const handleInputChange = (_: React.SyntheticEvent, newInputValue: string) => {
      setOptionsOpen(Boolean(newInputValue) && newInputValue !== tempFilterText);
      setTempFilterText(newInputValue);
    };

    const handleDateChange = (newDate: Dayjs | null) => {
      setOptionsOpen(false);
      if (newDate) {
        const dayjsObject = dayjs(newDate);
        setTempFilterText({ date: getDateValue(dayjsObject, String(tempFilterOption)) });
      }
    };

    const handleBetweenStartDateChange = (startDate: Dayjs | null) => {
      setOptionsOpen(false);
      if (startDate) {
        const dayjsObject = dayjs(startDate);
        setTempFilterText((prev) => ({ ...asDateText(prev), startDate: dayjsObject }));
      }
    };

    const handleBetweenEndDateChange = (endDate: Dayjs | null) => {
      setOptionsOpen(false);
      if (endDate) {
        const dayjsObject = dayjs(endDate);
        setTempFilterText((prev) => ({ ...asDateText(prev), endDate: dayjsObject }));
      }
    };

    interface RemoteDataResponse {
      result?: FilterRow[];
      count?: number;
    }

    const fetchRemoteData = async (
      remoteDataSource: string,
      query: Props["query"],
      beforeDataBound?: Props["beforeDataBound"],
    ) => {
      const modifiedQuery = { ...query, skip: 0, take: 1000, requiresCounts: true };
      // Dynamic remoteDataSource URL - endpoint not known at compile time
      const response = await axiosWrapper.post<RemoteDataResponse>(remoteDataSource, { value: modifiedQuery });
      const bd = beforeDataBound?.(response);
      return toFilterRows(bd) ?? response.data.result;
    };

    const fetchRemoteFilteredData = async (
      remoteDataSource: string,
      query: Props["query"],
      filterConditions: FilterConditionsMap,
      currentColumnId: string,
      filterOption: string,
      tempFilterText: FilterText,
      beforeDataBound?: Props["beforeDataBound"],
    ) => {
      // Only `text`/`predicate` matter to `getUpdatedFilterConditions` on this path, and the rest is
      // deliberately left off: supplying `type` would send this column down the grid's
      // date-splitting branch and change the query that fills the option list. `FilterCondition` is
      // shared with SlickGrid/index.tsx and declares every member required.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- option-list query: adding the required `id`/`type`/`option` would send this column down the date-split branch of getUpdatedFilterConditions (SlickGrid/index.tsx:1178) and duplicate its predicate
      const updatedConditions = {
        ...filterConditions,
        [currentColumnId]: {
          text: tempFilterText,
          predicate: [{ field: currentColumnId, operator: filterOption, value: tempFilterText }],
        },
      } as FilterConditionsMap;
      const updatedFilterConditions = getUpdatedFilterConditions(updatedConditions);
      const predicate = getFilterPredicates(updatedFilterConditions, false);
      const modifiedQuery = { ...query, skip: 0, take: 20, requiresCounts: true };
      modifiedQuery.where = query?.where?.length ? [...query.where, predicate] : [predicate];
      // Dynamic remoteDataSource URL - endpoint not known at compile time
      const response = await axiosWrapper.post<RemoteDataResponse>(remoteDataSource, { value: modifiedQuery });
      const bd = beforeDataBound?.(response);
      return toFilterRows(bd) ?? response.data.result;
    };

    const extractMultiSelectValues = (
      filteredData: FilterRow[],
      field: string,
      valueGetter?: ColumnDefinition["_valueGetter"],
    ) => {
      const allValues: string[] = [];
      for (const data of filteredData) {
        const fieldValue: unknown = valueGetter ? valueGetter(data) : get(data, field);
        if (Array.isArray(fieldValue)) {
          const items: unknown[] = fieldValue;
          for (const item of items) {
            // Falsy entries are dropped by the `filter(Boolean)` below, so `""` stands in for them.
            allValues.push(readName(item) || (item ? String(item) : ""));
          }
        } else if (fieldValue) {
          allValues.push(readName(fieldValue) || String(fieldValue));
        }
      }
      const unique = [...new Set(allValues.filter(Boolean))] as string[];
      return unique.length ? unique.sort((a, b) => a.localeCompare(b)) : ["No Records Found"];
    };

    const extractSingleSelectValues = (
      filteredData: FilterRow[],
      field: string,
      valueGetter?: ColumnDefinition["_valueGetter"],
    ) => {
      const values = [
        ...new Set(
          filteredData.map((data) => {
            const val: unknown = valueGetter ? valueGetter(data) : get(data, field);
            if (val == null) return "";
            return String(val);
          }),
        ),
      ] as string[];
      return values.length ? values : ["No Records Found"];
    };

    const fetchOptions = async (text: FilterText, field: string, filterOption: string) => {
      if (!filterType) return;
      const shouldFetch = filterType === "MultiSelect" || tempFilterOption === "equal" || Boolean(text);
      if (!shouldFetch) return;
      setLoading(true);
      let filteredData: FilterRow[] | undefined;
      if (filterType === "MultiSelect") {
        if (remoteDataSource && query) {
          filteredData = await fetchRemoteData(remoteDataSource, query, beforeDataBound);
        } else if (localData) {
          filteredData = datasetForFetchingOptions;
        }
      } else if (remoteDataSource && query) {
        filteredData = await fetchRemoteFilteredData(
          remoteDataSource,
          query,
          filterConditions,
          currentColumnId,
          filterOption,
          tempFilterText,
          beforeDataBound,
        );
      } else if (localData) {
        filteredData = applyFilter(datasetForFetchingOptions, currentColumnId, filterOption, text, filterType);
      }
      const valueGetter = columnDefinitions.find((col) => col.field === currentColumnId)?._valueGetter;
      if (filterType === "MultiSelect") {
        const optionsList = extractMultiSelectValues(filteredData ?? [], field, valueGetter);
        setOptions(optionsList);
        setMultiSelectFilteredOptions(optionsList);
      } else {
        const optionsList = extractSingleSelectValues(filteredData ?? [], field, valueGetter);
        setOptions(optionsList);
      }
      setLoading(false);
    };

    const getFilterOptions = () => {
      if (filterType) {
        return FILTER_MAPPER[filterType].map((optionKey) => (
          <MenuItem key={optionKey} value={optionKey}>
            {FILTER_OPTION_LABELS[optionKey]}
          </MenuItem>
        ));
      }
      return null;
    };

    const isTextFieldDisabled = tempFilterOption ? TEXT_FIELD_DISABLED_OPTIONS.has(tempFilterOption) : false;
    const tempDateText = asDateText(tempFilterText);
    const datePickerValueLeft =
      tempFilterOption === "between" ? tempDateText?.startDate || dayjs() : tempDateText?.date || dayjs();
    const datePickerValueRight = tempDateText?.endDate || dayjs();
    const popoverWidth = filterType === "Date" && tempFilterOption === "between" ? "22.5rem" : "15.625rem";

    return (
      <div>
        {filterType && (
          <Popover
            id={popoverId}
            open={Boolean(anchorEl)}
            anchorReference="anchorPosition"
            anchorPosition={anchorEl || { top: 0, left: 0 }}
            onClose={handleClosePopover}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            sx={{
              "& .MuiPaper-root": {
                boxShadow: "0rem 0.25rem 0.75rem rgba(0, 0, 0, 0.1)",
                borderRadius: "0.5rem",
                padding: "1.25rem",
                width: `${popoverWidth}`,
                marginTop: "0.625rem",
                marginInlineStart: "0.625rem",
              },
            }}
          >
            <div>
              <FormControl fullWidth>
                <Select
                  value={tempFilterOption}
                  displayEmpty
                  onChange={handleFilterChange}
                  style={{ marginBottom: "1rem" }}
                >
                  {getFilterOptions()}
                </Select>
              </FormControl>
              {(filterType === "Text" || filterType === "Number") && (
                <Autocomplete
                  freeSolo
                  open={optionsOpen}
                  options={options}
                  onClose={handleCloseOptions}
                  getOptionDisabled={(option) => option === "No Records Found"}
                  getOptionLabel={(option) => (typeof option === "string" ? option : "")}
                  value={typeof tempFilterText === "string" ? tempFilterText : String(tempFilterText ?? "")}
                  filterOptions={(x) => x}
                  onInputChange={handleInputChange}
                  disabled={isTextFieldDisabled}
                  renderOption={(props, option) => (
                    <li {...props} style={{ minWidth: "12.5rem" }}>
                      {renderOption && renderOption(props, option, currentColumnId)
                        ? renderOption(props, option, currentColumnId)
                        : option || (
                            <em>
                              <Text>{t("empty")}</Text>
                            </em>
                          )}
                    </li>
                  )}
                  loading={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id={`${isTextFieldDisabled ? "filled-disabled" : "outlined-helperText"}`}
                      disabled={isTextFieldDisabled}
                      placeholder={t("Enter the Value")}
                      type={filterType === "Number" ? "number" : "text"}
                      onClick={() => {
                        if (tempFilterOption === "equal") {
                          setOptionsOpen(true);
                        }
                      }}
                    />
                  )}
                />
              )}
              {filterType === "MultiSelect" && (
                <Autocomplete
                  multiple
                  open={optionsOpen}
                  options={multiSelectFilteredOptions.length > 0 ? multiSelectFilteredOptions : options}
                  onClose={handleCloseOptions}
                  getOptionDisabled={(option) => option === "No Records Found"}
                  getOptionLabel={(option) => (typeof option === "string" ? option : "")}
                  value={toMultiSelectChips(tempFilterText)}
                  filterOptions={(x) => x}
                  inputValue={multiSelectSearchValue}
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === "input") {
                      handleSearchInMultiSelectOptions(newInputValue);
                      setOptionsOpen(true);
                    }
                  }}
                  onChange={(_, newValue) => {
                    setTempFilterText(newValue.filter((val) => val !== "No Records Found"));
                    setMultiSelectSearchValue("");
                    setMultiSelectFilteredOptions(options);
                  }}
                  disableCloseOnSelect
                  renderOption={(props, option, { selected }) => (
                    <li {...props} style={{ minWidth: "12.5rem" }}>
                      <Checkbox
                        style={{ marginInlineEnd: "0.5rem" }}
                        checked={selected}
                        disabled={option === "No Records Found"}
                      />
                      {option || (
                        <em>
                          <Text>{t("empty")}</Text>
                        </em>
                      )}
                    </li>
                  )}
                  loading={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t("Enter the value")}
                      onClick={() => {
                        setOptionsOpen(true);
                        if (multiSelectFilteredOptions.length === 0 && options.length > 0) {
                          setMultiSelectFilteredOptions(options);
                        }
                      }}
                    />
                  )}
                />
              )}
              {filterType === "Date" && (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <div style={{ display: "flex", gap: "1rem" }}>
                    <DatePicker
                      disabled={tempFilterOption ? TEXT_FIELD_DISABLED_OPTIONS.has(tempFilterOption) : false}
                      onChange={tempFilterOption === "between" ? handleBetweenStartDateChange : handleDateChange}
                      value={datePickerValueLeft}
                    />
                    {tempFilterOption === "between" && (
                      <DatePicker onChange={handleBetweenEndDateChange} value={datePickerValueRight} />
                    )}
                  </div>
                </LocalizationProvider>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "0.625rem",
                  paddingTop: "1rem",
                }}
              >
                <ButtonComponent
                  buttonType="gradientButton"
                  style={{ flex: "1", height: "2.1875rem", borderRadius: "0.5rem" }}
                  onClick={() => handleApplyFilter(false)}
                  data-testid="applyFilter"
                >
                  <Text>{t("Apply")}</Text>
                </ButtonComponent>
                <ButtonComponent
                  buttonType="borderButton"
                  style={{ flex: "1", height: "2.1875rem", borderRadius: "0.5rem" }}
                  onClick={() => handleApplyFilter(true)}
                  data-testid="clearFilter"
                >
                  <Text>{t("Clear")}</Text>
                </ButtonComponent>
              </div>
            </div>
          </Popover>
        )}
      </div>
    );
  },
);

FilterComponent.displayName = "FilterComponent";
export default FilterComponent;
